// Modal "Receber Avulso" — recebe dinheiro de um CLIENTE FORA de uma venda normal (sem NF, sem
// orçamento), em QUALQUER das 4 formas. O dinheiro cai LIVRE na conta certa e é abatido depois nos
// títulos de crediário em Contas a Receber (sem contar duas vezes). Generaliza o antigo "Cobrar Pix
// Avulso" (DA-222). Regra do Ruan: o sistema nunca bloqueia o recebimento por forma nenhuma.
//
//  - Dinheiro      → instantâneo (gaveta/turno do operador). POST /api/receber-avulso/dinheiro.
//  - Pix           → QR estático do MP (reusa POST /api/pix/cobrar-avulso, DA-222). Cai livre em mp_recebimentos.
//  - Crédito/Débito→ maquininha Point (1x, valor digitado). POST /api/receber-avulso/cartao/cobrar + poll.
//
// Cliente é OBRIGATÓRIO e cadastrado. Todos caem LIVRES e ficam VINCULÁVEIS em Contas a Receber.
import { useEffect, useState } from 'react';
import { Loader2, Check, X, Search, Banknote, QrCode, CreditCard } from 'lucide-react';
import { api } from '../../lib/api';
import { Modal } from '../Modal';
import { MoneyInput } from '../inputs/MoneyInput';
import { ModalBuscarCliente } from '../orcamento/ModalBuscarCliente';
import { CobrancaPix } from './CobrancaPix';
import { CobrancaCartao, ImprimirViaCartao } from './CobrancaCartao';
import { useToast } from '../Toast';
import { formatMoeda } from '@fiodecobre/shared';

interface ClienteSel { id: number; nome: string; codigo_legado?: string | null; razao_social?: string | null; }

type Forma = 'dinheiro' | 'pix' | 'cartao_credito' | 'cartao_debito';

const FORMAS: { id: Forma; rotulo: string; icone: React.ReactNode }[] = [
  { id: 'dinheiro', rotulo: 'Dinheiro', icone: <Banknote size={16} /> },
  { id: 'pix', rotulo: 'Pix', icone: <QrCode size={16} /> },
  { id: 'cartao_credito', rotulo: 'Crédito', icone: <CreditCard size={16} /> },
  { id: 'cartao_debito', rotulo: 'Débito', icone: <CreditCard size={16} /> },
];

export function ModalReceberAvulso({ onClose, onRecebido }: { onClose: () => void; onRecebido?: () => void }) {
  const toast = useToast();
  const [cliente, setCliente] = useState<ClienteSel | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [forma, setForma] = useState<Forma>('dinheiro');
  const [valor, setValor] = useState(0);
  const [enviando, setEnviando] = useState(false);
  // Disponibilidade das formas: Pix (provedor gratuito) e cartão (Point) podem estar off.
  const [pixGratuito, setPixGratuito] = useState(false);
  const [pointOk, setPointOk] = useState(false);

  // Fluxo Pix: agora pela PEÇA ÚNICA <CobrancaPix> (mesma do Finalizar Venda). Ela faz o ciclo todo
  // (QR → acompanha → confirma sozinho/manual → pago) e devolve a bola por callback. Aqui o alvo é
  // avulso (amarrado ao cliente): o recebimento cai LIVRE (não vincula — abate na mão em Contas a
  // Receber). Ganha o que o avulso não tinha: bloqueio do terminal, confirmação do pagador e comprovante.
  const [pixIniciado, setPixIniciado] = useState(false); // monta o <CobrancaPix> (que cobra ao montar)
  const [pixPago, setPixPago] = useState(false);
  // Cartão: agora pela PEÇA ÚNICA <CobrancaCartao> (mesma do Finalizar Venda). Ela faz o ciclo na
  // maquininha (cobra ao montar → aguarda → aprova / tenta de novo) e devolve a bola por callback.
  // Aqui o alvo é avulso: o recebimento cai LIVRE (abate na mão em Contas a Receber).
  const [cartaoIniciado, setCartaoIniciado] = useState(false); // monta o <CobrancaCartao>
  const [cartaoPago, setCartaoPago] = useState(false);
  const [imprimirVia, setImprimirVia] = useState(false); // papel na maquininha só se o cliente pedir
  // Valor antes da cobrança (o Pix calibra 5,00 → 5,01): ao voltar pro form, restaura o original.
  const [valorAntes, setValorAntes] = useState(0);

  // "Cancelar" no avulso VOLTA pro formulário (não fecha o modal): desmonta a peça (que cancela a
  // cobrança no desmonte) e restaura o valor digitado. Sair de vez é só pelo X / "Fechar".
  function voltarProForm() {
    setValor(valorAntes);
    setPixIniciado(false);
    setPixPago(false);
    setCartaoIniciado(false);
    setCartaoPago(false);
  }

  const [recebidoDinheiro, setRecebidoDinheiro] = useState<{ caixa_nome: string } | null>(null);

  useEffect(() => {
    api.get<{ gratuito: boolean }>('/api/pix/status').then((r) => setPixGratuito(!!r.gratuito)).catch(() => setPixGratuito(false));
    api.get<{ point: boolean }>('/api/receber-avulso/formas').then((r) => setPointOk(!!r.point)).catch(() => setPointOk(false));
  }, []);

  // Pix pago (confirma sozinho): mostra "Pix recebido" por um instante e VOLTA pro formulário — limpa o
  // valor, mantém o cliente, pronto pro próximo. Igual ao cancelar (não fica preso na tela de sucesso).
  // O cliente vê o verde na tela do balcão; o operador tem o toast. Quem quiser sair antes usa o X.
  useEffect(() => {
    if (!pixPago) return;
    const t = setTimeout(() => { setValor(0); setPixIniciado(false); setPixPago(false); }, 2500);
    return () => clearTimeout(t);
  }, [pixPago]);

  const formaIndisponivel = (forma === 'pix' && !pixGratuito)
    || ((forma === 'cartao_credito' || forma === 'cartao_debito') && !pointOk);

  // Por que o botão está apagado — mostrado no rodapé pra não deixar o usuário no escuro.
  // (forma indisponível já tem o aviso laranja próprio no corpo.)
  const motivoDesabilitado = !cliente ? 'Escolha o cliente'
    : !(valor > 0) ? 'Informe o valor'
    : null;

  async function confirmar() {
    if (!cliente) { toast.erro('Escolha o cliente'); return; }
    if (!(valor > 0)) { toast.erro('Informe o valor'); return; }
    setEnviando(true);
    try {
      if (forma === 'dinheiro') {
        const r = await api.post<{ caixa_nome: string }>('/api/receber-avulso/dinheiro', { cliente_id: cliente.id, valor });
        setRecebidoDinheiro({ caixa_nome: r.caixa_nome });
        onRecebido?.();
      } else if (forma === 'pix') {
        setValorAntes(valor);     // guarda o original pra restaurar se cancelar (o Pix calibra o centavo)
        setPixIniciado(true);     // monta o <CobrancaPix>, que cobra ao montar e conduz o ciclo
      } else {
        setValorAntes(valor);     // guarda o original pra restaurar se voltar pro form
        setCartaoIniciado(true);  // monta o <CobrancaCartao>, que cobra ao montar e conduz o ciclo
      }
    } catch (e: any) { toast.erro(e.message); }
    finally { setEnviando(false); }
  }

  const sucessoLivre = (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '20px 0' }}>
      <div style={{ width: 90, height: 90, borderRadius: '50%', background: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Check size={52} color="#fff" strokeWidth={3} />
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, color: '#166534' }}>RECEBIMENTO CONFIRMADO</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', maxWidth: 320 }}>
        Esse valor ficou <strong>livre</strong>. Abata nos títulos do cliente em <strong>Contas a Receber</strong> (baixa parcial → Vincular ao recebimento).
      </div>
    </div>
  );

  // --- DINHEIRO confirmado ---
  if (recebidoDinheiro) {
    return (
      <Modal aberto titulo="Receber Avulso" onFechar={onClose} largura={460}
        rodape={<div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><button type="button" className="btn-primario" onClick={onClose}>Fechar</button></div>}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{cliente?.razao_social || cliente?.nome}</div>
          <div style={{ fontSize: 32, fontWeight: 800 }}>{formatMoeda(valor)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Caixa: <strong>{recebidoDinheiro.caixa_nome}</strong></div>
          {sucessoLivre}
        </div>
      </Modal>
    );
  }

  // --- PIX (QR / confirmar pagador / pago) — agora pela PEÇA ÚNICA <CobrancaPix layout="cheio"/> ---
  if (pixIniciado && cliente) {
    // Cliente do cabeçalho: o nome escolhido (a cobrança não devolve nome no avulso).
    const clienteNome = cliente.razao_social || cliente.nome || '';
    // "Cancelar QR" volta pro formulário (voltarProForm); o X / "Fechar" saem do modal. Em ambos o
    // <CobrancaPix> cancela o QR no desmonte se ainda pendente.
    return (
      <Modal aberto titulo="Receber Avulso — Pix" onFechar={pixPago ? onClose : voltarProForm} largura={460}
        rodape={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {pixPago ? <button type="button" className="btn-primario" onClick={onClose}>Fechar</button>
              : <button type="button" className="btn-secundario" onClick={voltarProForm}>
                <X size={14} style={{ display: 'inline', marginBottom: -2 }} /> Cancelar QR
              </button>}
          </div>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{clienteNome}</div>
          <div style={{ fontSize: 32, fontWeight: 800 }}>{formatMoeda(valor)}</div>
          <div style={{ width: '100%' }}>
            <CobrancaPix
              alvo={{ tipo: 'avulso', clienteId: cliente.id }}
              valor={valor}
              layout="cheio"
              mostrarComprovante
              onPago={() => { setPixPago(true); toast.sucesso('Pix recebido — fica livre pra abater no Contas a Receber'); onRecebido?.(); }}
              onValorAjustado={(v) => setValor(v)}
              onCancelado={voltarProForm}
            />
          </div>
        </div>
      </Modal>
    );
  }

  // --- CARTÃO (na maquininha / aprovado) — agora pela PEÇA ÚNICA <CobrancaCartao layout="cheio"/> ---
  if (cartaoIniciado && cliente) {
    const clienteNome = cliente.razao_social || cliente.nome || '';
    // "Cancelar" volta pro formulário (voltarProForm); o X / "Fechar" saem do modal. Em ambos a peça
    // cancela a cobrança no desmonte se ainda na maquininha.
    return (
      <Modal aberto titulo="Receber Avulso — Cartão" onFechar={cartaoPago ? onClose : voltarProForm} largura={460}
        rodape={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {cartaoPago ? <button type="button" className="btn-primario" onClick={onClose}>Fechar</button>
              : <button type="button" className="btn-secundario" onClick={voltarProForm}>
                <X size={14} style={{ display: 'inline', marginBottom: -2 }} /> Cancelar
              </button>}
          </div>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{clienteNome} · {forma === 'cartao_debito' ? 'Débito' : 'Crédito'}</div>
          <div style={{ fontSize: 32, fontWeight: 800 }}>{formatMoeda(valor)}</div>
          <div style={{ width: '100%' }}>
            <CobrancaCartao
              alvo={{ tipo: 'avulso', clienteId: cliente.id }}
              valor={valor}
              cardType={forma === 'cartao_debito' ? 'debit' : 'credit'}
              layout="cheio"
              onAprovado={() => { setCartaoPago(true); toast.sucesso('Cartão aprovado — fica livre pra abater no Contas a Receber'); onRecebido?.(); }}
              onCancelado={voltarProForm}
              imprimirVia={imprimirVia}
            />
          </div>
        </div>
      </Modal>
    );
  }

  // --- Tela inicial: cliente + forma + valor ---
  return (
    <>
      <Modal aberto titulo="Receber Avulso" onFechar={onClose} largura={460}
        rodape={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#b45309' }}>
              {!formaIndisponivel && motivoDesabilitado ? `↳ ${motivoDesabilitado} pra continuar` : ''}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn-secundario" onClick={onClose} disabled={enviando}>Cancelar</button>
              <button type="button" className="btn-primario" onClick={() => void confirmar()}
                disabled={enviando || !cliente || !(valor > 0) || formaIndisponivel}>
                {enviando ? <Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} />
                  : forma === 'dinheiro' ? 'Receber' : forma === 'pix' ? 'Gerar QR Pix' : 'Cobrar na maquininha'}
              </button>
            </div>
          </div>
        }>
        <div style={{ display: 'grid', gap: 16, padding: '4px 0' }}>
          <div style={{ backgroundColor: '#fde68a', color: '#000', borderRadius: 8, padding: '10px 12px', fontSize: 13, lineHeight: 1.5 }}>
            Recebe um valor <strong>livre</strong> amarrado ao cliente, fora de uma venda. O dinheiro cai <strong>livre</strong> — depois você abate nos títulos em Contas a Receber.
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Cliente <span style={{ color: 'var(--danger,#dc2626)' }}>*</span></label>
            <button type="button" onClick={() => setBuscando(true)}
              style={{ width: '100%', textAlign: 'left', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--input-border,#d1d5db)', background: 'var(--input-bg)', color: 'var(--input-fg)', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
              <Search size={14} />
              {cliente ? <strong>{cliente.razao_social || cliente.nome}</strong> : <span style={{ color: 'var(--text-muted)' }}>Buscar cliente…</span>}
            </button>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Forma de pagamento <span style={{ color: 'var(--danger,#dc2626)' }}>*</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginTop: 4 }}>
              {FORMAS.map((f) => {
                const ativo = forma === f.id;
                const off = (f.id === 'pix' && !pixGratuito) || ((f.id === 'cartao_credito' || f.id === 'cartao_debito') && !pointOk);
                return (
                  <button key={f.id} type="button" onClick={() => setForma(f.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      border: ativo ? '2px solid #d97706' : '1px solid var(--input-border,#d1d5db)',
                      background: ativo ? '#fde68a' : 'var(--input-bg)', color: ativo ? '#000' : 'var(--input-fg)',
                      fontWeight: ativo ? 700 : 500,
                    }}
                    title={off ? (f.id === 'pix' ? 'Pix custo-zero indisponível' : 'Maquininha Point não configurada') : undefined}>
                    {f.icone} {f.rotulo}
                    {off && <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 'auto' }}>off</span>}
                  </button>
                );
              })}
            </div>
            {formaIndisponivel && (
              <div style={{ fontSize: 11, color: '#b45309', marginTop: 6 }}>
                {forma === 'pix' ? 'Pix custo-zero indisponível agora — escolha outra forma.' : 'Maquininha Point não configurada — escolha outra forma.'}
              </div>
            )}
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Valor <span style={{ color: 'var(--danger,#dc2626)' }}>*</span></label>
            <MoneyInput valor={valor} onChangeValor={setValor}
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, fontSize: 16, fontWeight: 700, textAlign: 'right', marginTop: 4 }} />
            {(forma === 'cartao_credito' || forma === 'cartao_debito') && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Cobrança 1x na maquininha. Se houver juro, digite o valor já com o juro calculado por fora.
              </div>
            )}
            {(forma === 'cartao_credito' || forma === 'cartao_debito') && (
              <div style={{ marginTop: 8 }}>
                <ImprimirViaCartao marcado={imprimirVia} onChange={setImprimirVia} />
              </div>
            )}
          </div>
        </div>
      </Modal>
      {buscando && (
        <ModalBuscarCliente
          onSelect={(c) => { setCliente(c as ClienteSel); setBuscando(false); }}
          onClose={() => setBuscando(false)}
        />
      )}
    </>
  );
}
